// src/components/roles/RolesPage/RolesPage.hooks.ts
import { useState, useEffect, useCallback } from 'react';
import { SelectChangeEvent } from '@mui/material';
import {
  Role,
  RoleFilter,
  RolePaginationState,
  CreateRoleDto,
  UpdateRoleDto,
} from '../../../types/role.types';
import { usePageState } from '../../../hooks/usePageState';
import { RolesService } from '../../../api';
import { RolesServiceHelpers } from '../../../utils/api-helpers/RolesService.helpers';
import { mapApiRoleToRole } from '../../../types/role.types';
import {
  downloadCsvExport,
  csvExportDateSuffix,
  CsvExportError,
} from '../../../utils/csvExport/downloadCsvExport';

// Interfaz para el estado persistente
interface RolesPageState {
  filters: RoleFilter;
  showMoreFilters: boolean;
  pagination: RolePaginationState;
  selectedRoles: string[];
}

export interface UseRolesPageReturn {
  // Filter states
  filters: RoleFilter;
  setFilters: React.Dispatch<React.SetStateAction<RoleFilter>>;
  appliedFilters: RoleFilter;
  showMoreFilters: boolean;
  setShowMoreFilters: React.Dispatch<React.SetStateAction<boolean>>;
  handleClearAppliedField: (patch: Partial<RoleFilter>) => void;

  // Pagination states
  pagination: RolePaginationState;
  setPagination: React.Dispatch<React.SetStateAction<RolePaginationState>>;

  // Drawer states
  drawerOpen: boolean;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedRole: Role | null;
  setSelectedRole: React.Dispatch<React.SetStateAction<Role | null>>;

  // Modal states
  detailModalOpen: boolean;
  setDetailModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  detailRole: Role | null;
  setDetailRole: React.Dispatch<React.SetStateAction<Role | null>>;
  deleteModalOpen: boolean;
  setDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  rolesToDelete: Role[];
  setRolesToDelete: React.Dispatch<React.SetStateAction<Role[]>>;
  blockModalOpen: boolean;
  setBlockModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  roleToBlock: Role | null;
  setRoleToBlock: React.Dispatch<React.SetStateAction<Role | null>>;

  // Role data
  roles: Role[];
  setRoles: React.Dispatch<React.SetStateAction<Role[]>>;
  selectedRoles: string[];
  setSelectedRoles: React.Dispatch<React.SetStateAction<string[]>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;

  // Notification state
  notification: {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
    successMessage?: string;
    errorMessage?: string;
  };
  setNotification: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      message: string;
      severity: 'success' | 'error' | 'warning' | 'info';
      successMessage?: string;
      errorMessage?: string;
    }>
  >;

  // Handlers
  handleSearch: () => void;
  handleReset: () => void;
  handleFilterChange: (newFilters: Partial<RoleFilter>) => void;
  handlePaginationChange: (newPagination: Partial<RolePaginationState>) => void;
  handleItemsPerPageChange: (event: SelectChangeEvent<unknown>) => void;
  handleRoleSelect: (roleId: string, selected: boolean) => void;
  handleSelectAll: (selected: boolean) => void;
  handleAddRole: () => void;
  handleEditRole: (roleId: string) => void;
  handleViewRole: (roleId: string) => void;
  handleDeleteRoles: () => void;
  handleDeleteSingleRole: (id: string) => void;
  handleConfirmDelete: () => Promise<void>;
  handleCloseDeleteModal: () => void;
  handleBlockRole: (roleId: string, isBlocked: boolean) => void;
  handleBlockRoleDirect: (roleId: string) => Promise<void>;
  handleUnblockRole: (roleId: string) => Promise<void>;
  handleConfirmBlock: (reason?: string) => Promise<void>;
  handleCloseBlockModal: () => void;
  handleCloseDrawer: () => void;
  handleSaveRole: (roleData: CreateRoleDto | UpdateRoleDto) => Promise<void>;
  handleCloseDetailModal: () => void;
  handleCloseNotification: () => void;
  handleExportRoles: () => Promise<void>;
  handleCopyRole: (roleId: string) => Promise<void>;
}

const emptyRoleFilters = (): RoleFilter => ({
  searchTerm: '',
  status: 'all',
  startDate: undefined,
  endDate: undefined,
  sortBy: 'createdAt',
});

/** Drop unsupported sort values from persisted state (e.g. legacy updatedAt). */
const normalizeRoleFilters = (filters: RoleFilter): RoleFilter => {
  const allowedSort = ['name', 'createdAt', 'userCount'] as const;
  const sortBy =
    filters.sortBy && (allowedSort as readonly string[]).includes(filters.sortBy)
      ? (filters.sortBy as RoleFilter['sortBy'])
      : 'createdAt';
  return { ...filters, sortBy };
};

export const useRolesPage = (): UseRolesPageReturn => {
  // Estado persistente de la página
  const { state, saveState } = usePageState<RolesPageState>();

  // Draft vs applied filters
  const initialFilters = normalizeRoleFilters(state.filters || emptyRoleFilters());
  const [filters, setFilters] = useState<RoleFilter>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<RoleFilter>(initialFilters);
  const [showMoreFilters, setShowMoreFilters] = useState<boolean>(state.showMoreFilters || false);

  // Pagination states - inicializado desde el estado persistente o con valores por defecto
  const [pagination, setPagination] = useState<RolePaginationState>(
    state.pagination || {
      itemsPerPage: 10,
      currentPage: 1,
      totalItems: 0,
    },
  );

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  // Modal states
  const [detailModalOpen, setDetailModalOpen] = useState<boolean>(false);
  const [detailRole, setDetailRole] = useState<Role | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [rolesToDelete, setRolesToDelete] = useState<Role[]>([]);
  const [blockModalOpen, setBlockModalOpen] = useState<boolean>(false);
  const [roleToBlock, setRoleToBlock] = useState<Role | null>(null);

  // Role data
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>(state.selectedRoles || []);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Notification state
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
    successMessage?: string;
    errorMessage?: string;
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Add a search trigger state to control when searches are executed
  const [searchTrigger, setSearchTrigger] = useState(0);

  // Guardar el estado cuando cambie
  useEffect(() => {
    saveState({
      filters,
      showMoreFilters,
      pagination,
      selectedRoles,
    });
  }, [filters, showMoreFilters, pagination, selectedRoles, saveState]);

  // Initial search trigger
  useEffect(() => {
    setSearchTrigger(1);
  }, []);

  // Fetch roles method
  const fetchRoles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // El helper RolesServiceHelpers aplica sortBy soportado por la API
      const response = await RolesServiceHelpers.rolesControllerFindAllWithFilters(
        pagination.currentPage,
        pagination.itemsPerPage,
        appliedFilters,
      );

      if (response && response.data) {
        // Map API roles to Role interface
        const rolesData = response.data.map((apiRole: any) => mapApiRoleToRole(apiRole));

        setRoles(rolesData);

        // Update pagination with total items
        const totalItems = response.meta?.total ?? rolesData.length;
        setPagination((prev) => ({
          ...prev,
          totalItems,
        }));
      } else {
        setRoles([]);
        setPagination((prev) => ({
          ...prev,
          totalItems: 0,
        }));
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      setError('No se pudieron cargar los roles. Intente de nuevo.');
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.itemsPerPage, appliedFilters]);

  // Fetch roles when searchTrigger changes or pagination changes
  useEffect(() => {
    fetchRoles();
  }, [searchTrigger, pagination.currentPage, pagination.itemsPerPage, fetchRoles]);

  // Event handlers
  const handleSearch = useCallback(() => {
    setAppliedFilters(filters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
  }, [filters]);

  const handleReset = useCallback(() => {
    const empty = emptyRoleFilters();
    setFilters(empty);
    setAppliedFilters(empty);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
  }, []);

  const handleClearAppliedField = useCallback((patch: Partial<RoleFilter>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setAppliedFilters((prev) => ({ ...prev, ...patch }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
  }, []);

  const handleFilterChange = useCallback((newFilters: Partial<RoleFilter>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const handlePaginationChange = useCallback((newPagination: Partial<RolePaginationState>) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  }, []);

  const handleItemsPerPageChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const itemsPerPage = parseInt(event.target.value as string);
      handlePaginationChange({ itemsPerPage, currentPage: 1 });
    },
    [handlePaginationChange],
  );

  const handleRoleSelect = useCallback((roleId: string, selected: boolean) => {
    setSelectedRoles((prev) => (selected ? [...prev, roleId] : prev.filter((id) => id !== roleId)));
  }, []);

  const handleSelectAll = useCallback(
    (selected: boolean) => {
      if (selected) {
        setSelectedRoles(roles.map((r) => r.id));
      } else {
        setSelectedRoles([]);
      }
    },
    [roles],
  );

  const handleAddRole = useCallback(() => {
    setSelectedRole(null);
    setDrawerOpen(true);
  }, []);

  const handleEditRole = useCallback(
    (roleId: string) => {
      const role = roles.find((r) => r.id === roleId);
      if (role) {
        setSelectedRole(role);
        setDrawerOpen(true);
      }
    },
    [roles],
  );

  const handleViewRole = useCallback(
    (roleId: string) => {
      const role = roles.find((r) => r.id === roleId);
      if (role) {
        setDetailRole(role);
        setDetailModalOpen(true);
      }
    },
    [roles],
  );

  const handleDeleteRoles = useCallback(() => {
    if (selectedRoles.length === 0) return;

    // Get the roles to delete
    const rolesToDeleteList = roles.filter((r) => selectedRoles.includes(r.id));
    setRolesToDelete(rolesToDeleteList);
    setDeleteModalOpen(true);
  }, [selectedRoles, roles]);

  const handleDeleteSingleRole = useCallback(
    (id: string) => {
      const role = roles.find((r) => r.id === id);
      if (!role) return;

      setRolesToDelete([role]);
      setDeleteModalOpen(true);
    },
    [roles],
  );

  const handleConfirmDelete = useCallback(async () => {
    const roleIds = rolesToDelete.map((r) => r.id);

    if (roleIds.length === 0) {
      return;
    }

    try {
      // Delete roles one by one (backend may not support bulk delete yet)
      const deletePromises = roleIds.map((id) => RolesService.rolesControllerRemove(id));
      await Promise.all(deletePromises);

      setSearchTrigger((prev) => prev + 1); // Refresh the list
      setSelectedRoles([]);
      setDeleteModalOpen(false);
      setRolesToDelete([]);

      const successCount = roleIds.length;
      setNotification({
        open: true,
        message:
          successCount === 1
            ? 'Rol eliminado correctamente'
            : `${successCount} roles eliminados correctamente`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting roles:', error);
      setNotification({
        open: true,
        message: 'No se pudieron eliminar los roles. Intente de nuevo.',
        severity: 'error',
      });
    }
  }, [rolesToDelete]);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setRolesToDelete([]);
  }, []);

  const handleUnblockRole = useCallback(async (roleId: string) => {
    try {
      await RolesServiceHelpers.rolesControllerUnblock(roleId);
      setSearchTrigger((prev) => prev + 1); // Refresh the list
      setNotification({
        open: true,
        message: 'Rol desbloqueado correctamente',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error unblocking role:', error);
      setNotification({
        open: true,
        message: 'No se pudo desbloquear el rol. Intente de nuevo.',
        severity: 'error',
      });
    }
  }, []);

  const handleBlockRole = useCallback(
    (roleId: string, isBlocked: boolean) => {
      const role = roles.find((r) => r.id === roleId);
      if (!role) return;

      if (isBlocked) {
        // Block role
        setRoleToBlock(role);
        setBlockModalOpen(true);
      } else {
        // Unblock role (direct action, no confirmation needed)
        handleUnblockRole(roleId);
      }
    },
    [roles, handleUnblockRole],
  );

  // Bloquear rol directamente (sin modal, para usar desde el switch)
  const handleBlockRoleDirect = useCallback(async (roleId: string) => {
    try {
      await RolesServiceHelpers.rolesControllerBlock(roleId, {
        reason: undefined, // Sin razón cuando se bloquea desde el switch
      });
      setSearchTrigger((prev) => prev + 1); // Refresh the list
      setNotification({
        open: true,
        message: 'Rol bloqueado correctamente',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error blocking role:', error);
      setNotification({
        open: true,
        message: 'No se pudo bloquear el rol. Intente de nuevo.',
        severity: 'error',
      });
      throw error; // Re-throw para que el componente pueda manejarlo
    }
  }, []);

  const handleConfirmBlock = useCallback(
    async (reason?: string) => {
      if (!roleToBlock) return;

      try {
        await RolesServiceHelpers.rolesControllerBlock(roleToBlock.id, {
          reason,
        });
        setSearchTrigger((prev) => prev + 1); // Refresh the list
        setBlockModalOpen(false);
        setRoleToBlock(null);
        setNotification({
          open: true,
          message: 'Rol bloqueado correctamente',
          severity: 'success',
        });
      } catch (error) {
        console.error('Error blocking role:', error);
        setNotification({
          open: true,
          message: 'No se pudo bloquear el rol. Intente de nuevo.',
          severity: 'error',
        });
      }
    },
    [roleToBlock],
  );

  const handleCloseBlockModal = useCallback(() => {
    setBlockModalOpen(false);
    setRoleToBlock(null);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedRole(null);
  }, []);

  const handleSaveRole = useCallback(
    async (roleData: CreateRoleDto | UpdateRoleDto) => {
      try {
        if (selectedRole) {
          // Update existing role (metadata only — permissions via dedicated endpoint)
          const updatePayload: UpdateRoleDto = {
            name: roleData.name,
            description: roleData.description,
          };
          await RolesService.rolesControllerUpdate(selectedRole.id, updatePayload);

          if (roleData.permissions !== undefined) {
            await RolesServiceHelpers.rolesControllerUpdatePermissions(selectedRole.id, {
              permissions: roleData.permissions,
            });
          }

          setNotification({
            open: true,
            message: 'Rol actualizado correctamente',
            severity: 'success',
          });
        } else {
          // Create new role
          if (!roleData.name) {
            setNotification({
              open: true,
              message: 'El nombre del rol es requerido',
              severity: 'error',
            });
            return;
          }

          // El backend ya guarda los permisos en el create, no necesitamos llamar updatePermissions
          const createPayload: CreateRoleDto = {
            name: roleData.name,
            description: roleData.description,
            permissions: roleData.permissions, // Include permissions if provided
          } as any; // Temporal: el tipo auto-generado no incluye permissions pero el backend sí lo acepta

          await RolesService.rolesControllerCreate(createPayload);

          setNotification({
            open: true,
            message: 'Rol creado correctamente',
            severity: 'success',
          });
        }

        setDrawerOpen(false);
        setSelectedRole(null);
        // Forzar refresh de la lista para obtener la fecha actualizada
        setSearchTrigger((prev) => prev + 1); // Refresh the list
      } catch (error) {
        console.error('Error saving role:', error);
        setNotification({
          open: true,
          message: selectedRole
            ? 'No se pudo actualizar el rol. Intente de nuevo.'
            : 'No se pudo crear el rol. Intente de nuevo.',
          severity: 'error',
        });
        throw error; // Re-throw to let drawer handle it
      }
    },
    [selectedRole, setSearchTrigger], // Agregar setSearchTrigger a las dependencias
  );

  const handleCloseDetailModal = useCallback(() => {
    setDetailModalOpen(false);
    setDetailRole(null);
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification({
      open: false,
      message: '',
      severity: 'info',
      successMessage: undefined,
      errorMessage: undefined,
    });
  }, []);

  const handleExportRoles = useCallback(async () => {
    try {
      setNotification({
        open: true,
        message: 'Exportando roles...',
        severity: 'info',
      });

      await downloadCsvExport('/roles/export/csv', {}, `roles-${csvExportDateSuffix()}.csv`);

      setNotification({
        open: true,
        message: 'Roles exportados correctamente',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error exporting roles:', error);
      setNotification({
        open: true,
        message:
          error instanceof CsvExportError
            ? error.message
            : 'No se pudieron exportar los roles. Intente de nuevo.',
        severity: 'error',
      });
    }
  }, []);

  const handleCopyRole = useCallback(async (roleId: string) => {
    try {
      const copiedRole = await RolesServiceHelpers.rolesControllerCopy(roleId);
      setNotification({
        open: true,
        message: `Rol copiado correctamente: ${copiedRole.name}`,
        severity: 'success',
      });
      setSearchTrigger((prev) => prev + 1); // Refresh the list
    } catch (error) {
      console.error('Error copying role:', error);
      setNotification({
        open: true,
        message: 'No se pudo copiar el rol. Intente de nuevo.',
        severity: 'error',
      });
    }
  }, []);

  return {
    // Filter states
    filters,
    setFilters,
    appliedFilters,
    showMoreFilters,
    setShowMoreFilters,

    // Pagination states
    pagination,
    setPagination,

    // Drawer states
    drawerOpen,
    setDrawerOpen,
    selectedRole,
    setSelectedRole,

    // Modal states
    detailModalOpen,
    setDetailModalOpen,
    detailRole,
    setDetailRole,
    deleteModalOpen,
    setDeleteModalOpen,
    rolesToDelete,
    setRolesToDelete,
    blockModalOpen,
    setBlockModalOpen,
    roleToBlock,
    setRoleToBlock,

    // Role data
    roles,
    setRoles,
    selectedRoles,
    setSelectedRoles,
    loading,
    setLoading,
    error,
    setError,

    // Notification state
    notification,
    setNotification,

    // Handlers
    handleSearch,
    handleReset,
    handleClearAppliedField,
    handleFilterChange,
    handlePaginationChange,
    handleItemsPerPageChange,
    handleRoleSelect,
    handleSelectAll,
    handleAddRole,
    handleEditRole,
    handleViewRole,
    handleDeleteRoles,
    handleDeleteSingleRole,
    handleConfirmDelete,
    handleCloseDeleteModal,
    handleBlockRole,
    handleBlockRoleDirect,
    handleUnblockRole,
    handleConfirmBlock,
    handleCloseBlockModal,
    handleCloseDrawer,
    handleSaveRole,
    handleCloseDetailModal,
    handleCloseNotification,
    handleExportRoles,
    handleCopyRole,
  };
};
